<script setup lang="ts">
import type { AIProviderName, ModerationInput } from '@wilson/shared'
import { onMounted, ref } from 'vue'
import { api, jsonBody } from '../../services/api'
import { createBrowserAI } from '../../services/browser-ai'

interface Run {
  id: string
  executionMode: string
  provider: string
  model: string
  createdAt: string
  completedAt: string | null
  commentsScanned: number
  commentsFlagged: number
}
interface ResultRow {
  result: {
    commentId: string
    classification: string
    categories: string[]
    reason: string
    confidence?: number
  }
  message: string
}
const mode = ref<'browser' | 'server'>('browser')
const provider = ref<AIProviderName>(
  (localStorage.getItem('wilson.ai.provider') as AIProviderName) || 'ollama',
)
const baseUrl = ref(localStorage.getItem('wilson.ai.baseUrl') || 'http://localhost:11434')
const model = ref(localStorage.getItem('wilson.ai.model') || '')
const apiKey = ref(sessionStorage.getItem('wilson.ai.apiKey') || '')
const dateFrom = ref(new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 16))
const dateTo = ref(new Date().toISOString().slice(0, 16))
const bottleId = ref('')
const quoteId = ref('')
const bottles = ref<Array<{ id: string; name: string }>>([])
const quotes = ref<Array<{ id: string; name: string }>>([])
const discoveredModels = ref<string[]>([])
const runs = ref<Run[]>([])
const selectedRun = ref<{ run: Run; results: ResultRow[] } | null>(null)
const busy = ref(false)
const status = ref('')
const error = ref('')

function payload() {
  return {
    dateFrom: new Date(dateFrom.value).toISOString(),
    dateTo: new Date(dateTo.value).toISOString(),
    provider: provider.value,
    model: model.value,
    ...(bottleId.value ? { bottleId: bottleId.value } : {}),
    ...(quoteId.value ? { quoteId: quoteId.value } : {}),
  }
}
function saveBrowserSettings() {
  localStorage.setItem('wilson.ai.provider', provider.value)
  localStorage.setItem('wilson.ai.baseUrl', baseUrl.value)
  localStorage.setItem('wilson.ai.model', model.value)
  if (apiKey.value) sessionStorage.setItem('wilson.ai.apiKey', apiKey.value)
  else sessionStorage.removeItem('wilson.ai.apiKey')
}
async function loadRuns() {
  const data = await api<{ items: Run[] }>('/admin/moderation/runs')
  runs.value = data.items
}
async function loadFilters() {
  const [bottleData, quoteData] = await Promise.all([
    api<{ items: Array<{ id: string; name: string }> }>('/admin/bottles?pageSize=100'),
    api<{ items: Array<{ id: string; name: string }> }>('/admin/quotes?pageSize=100'),
  ])
  bottles.value = bottleData.items
  quotes.value = quoteData.items
}
function browserProvider() {
  saveBrowserSettings()
  return createBrowserAI({
    provider: provider.value,
    baseUrl: baseUrl.value,
    model: model.value,
    ...(apiKey.value ? { apiKey: apiKey.value } : {}),
  })
}
async function testBrowserConnection() {
  error.value = ''
  status.value = ''
  const ok = await browserProvider().testConnection()
  status.value = ok
    ? 'Browser provider connection successful.'
    : 'Browser provider did not respond.'
}
async function discoverBrowserModels() {
  error.value = ''
  status.value = ''
  try {
    const models = await browserProvider().listModels()
    discoveredModels.value = models.map(({ id }) => id)
    status.value = models.length ? `Found ${models.length} models.` : 'No models were returned.'
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'Models could not be discovered.'
  }
}
async function scan() {
  busy.value = true
  error.value = ''
  status.value = ''
  try {
    let runId: string
    if (mode.value === 'browser') {
      saveBrowserSettings()
      const started = await api<{ runId: string; comments: ModerationInput[] }>(
        '/admin/moderation/browser/start',
        { method: 'POST', ...jsonBody(payload()) },
      )
      runId = started.runId
      const ai = browserProvider()
      const results = started.comments.length ? await ai.moderate(started.comments) : []
      await api(`/admin/moderation/browser/${runId}/results`, {
        method: 'POST',
        ...jsonBody({ results }),
      })
    } else {
      const result = await api<{ runId: string }>('/admin/moderation/runs', {
        method: 'POST',
        ...jsonBody(payload()),
      })
      runId = result.runId
    }
    status.value = 'Moderation completed. Results are advisory until you review them.'
    await loadRuns()
    await showRun(runId)
  } catch (caught) {
    const message = caught instanceof Error ? caught.message : ''
    error.value =
      /^\s*\[/.test(message) || message.includes('invalid_value') || message.includes('ZodError')
        ? 'The AI returned malformed results. No moderation results were stored.'
        : message || 'Moderation could not be completed. No moderation results were stored.'
  } finally {
    busy.value = false
  }
}
async function showRun(id: string) {
  selectedRun.value = await api(`/admin/moderation/runs/${id}`)
}
async function decide(result: ResultRow, visible: boolean) {
  await api(`/admin/comments/${result.result.commentId}`, {
    method: 'PATCH',
    ...jsonBody({
      isVisible: visible,
      moderationStatus: visible
        ? result.result.classification === 'safe'
          ? 'safe'
          : 'review'
        : 'inappropriate',
    }),
  })
  status.value = visible ? 'Comment kept.' : 'Comment hidden.'
}
onMounted(() => Promise.all([loadRuns(), loadFilters()]))
</script>

<template>
  <header class="admin-heading">
    <div>
      <p class="eyebrow">Optional AI assistance</p>
      <h1>Moderation</h1>
      <p>Scan anonymous notes, then make every visibility decision yourself.</p>
    </div>
    <RouterLink class="button secondary" to="/admin/settings/ai">AI settings</RouterLink>
  </header>
  <p v-if="error" class="form-error banner">{{ error }}</p>
  <p v-if="status" class="notice">{{ status }}</p>
  <section class="admin-card moderation-form">
    <div class="form-grid">
      <label>From<input v-model="dateFrom" type="datetime-local" /></label
      ><label>To<input v-model="dateTo" type="datetime-local" /></label
      ><label
        >Bottle filter<select v-model="bottleId">
          <option value="">All bottles</option>
          <option v-for="bottle in bottles" :key="bottle.id" :value="bottle.id">
            {{ bottle.name }}
          </option>
        </select></label
      ><label
        >Quote filter<select v-model="quoteId">
          <option value="">All quotes</option>
          <option v-for="quote in quotes" :key="quote.id" :value="quote.id">
            {{ quote.name }}
          </option>
        </select></label
      ><label
        >Execution<select v-model="mode">
          <option value="browser">This browser</option>
          <option value="server">Server</option>
        </select></label
      ><label
        >Provider<select v-model="provider">
          <option value="ollama">Ollama</option>
          <option value="openai-compatible">OpenAI-compatible</option>
        </select></label
      ><template v-if="mode === 'browser'"
        ><label class="span-2">Endpoint<input v-model="baseUrl" type="url" /></label
        ><label
          >Model<input v-model="model" list="browser-models" required /><datalist
            id="browser-models"
          >
            <option v-for="name in discoveredModels" :key="name" :value="name" /></datalist></label
        ><label
          >API key <span class="optional">session only</span
          ><input v-model="apiKey" type="password" /></label></template
      ><label v-else class="span-2"
        >Configured server model<input v-model="model" required
      /></label>
    </div>
    <div v-if="mode === 'browser'" class="button-row">
      <button class="button secondary" type="button" @click="testBrowserConnection">
        Test browser connection</button
      ><button class="button secondary" type="button" @click="discoverBrowserModels">
        List browser models
      </button>
    </div>
    <button class="button primary" :disabled="busy || !model" @click="scan">
      {{ busy ? 'Scanning messages…' : 'Scan messages' }}
    </button>
    <p class="field-help">Only comment IDs and message text are sent to the selected provider.</p>
  </section>
  <div class="admin-grid moderation-grid">
    <section class="admin-card">
      <div class="card-heading"><h2>Recent runs</h2></div>
      <ol class="run-list">
        <li v-for="run in runs" :key="run.id">
          <button @click="showRun(run.id)">
            <span
              ><strong>{{ run.executionMode }} · {{ run.provider }}</strong
              ><small>{{ new Date(run.createdAt).toLocaleString() }}</small></span
            ><span>{{ run.commentsFlagged }}/{{ run.commentsScanned }} flagged</span>
          </button>
        </li>
      </ol>
    </section>
    <section v-if="selectedRun" class="admin-card">
      <div class="card-heading">
        <h2>Results</h2>
        <span>{{ selectedRun.results.length }}</span>
      </div>
      <div class="result-list">
        <article
          v-for="row in selectedRun.results"
          :key="row.result.commentId"
          :class="row.result.classification"
        >
          <header>
            <span class="status-pill" :class="row.result.classification">{{
              row.result.classification
            }}</span
            ><small>{{ row.result.categories.join(', ') }}</small>
          </header>
          <p>“{{ row.message }}”</p>
          <small>{{ row.result.reason }}</small>
          <div class="row-actions">
            <button @click="decide(row, true)">Keep</button
            ><button @click="decide(row, false)">Hide</button>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>
